import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { track, ANALYTICS_EVENTS } from "../../../utils/productAnalytics";

const bannerImages = [
  "/lp/banners/sample-fashion-grid.png",
  "/lp/banners/sample-skincare-aqua.png",
  "/lp/banners/sample-housing-01.png",
  "/lp/banners/sample-bellsales-visual.png",
  "/lp/banners/sample-salesbox-rpo-01.png",
  "/lp/banners/sample-sorndy.png",
];

const CheckIcon: React.FC = () => (
  <svg
    className="w-4 h-4 text-[#3eb298]"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const HeroSection: React.FC = () => {
  const publicUrl = process.env.PUBLIC_URL || "";

  return (
    <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
      {/* Subtle gradient background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 70% 20%, rgba(62,178,152,0.06) 0%, transparent 60%), radial-gradient(ellipse 50% 50% at 30% 80%, rgba(90,105,193,0.04) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-12 items-center">
          {/* Left: Copy */}
          <div className="max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center h-7 px-3 rounded-full bg-[#1d1926]/5 text-[#1d1926]/60 text-[13px] font-medium tracking-[0.04em]">
                AIバナー生成エージェント
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 text-[48px] md:text-[80px] font-extrabold leading-[0.95] tracking-[-0.04em]"
            >
              URLを入れるだけ。
              <br />
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, #3eb298, #5a69c1, #8f6fc2)",
                }}
              >
                広告バナーを
              </span>
              <br />
              AIが量産。
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-6 text-[18px] text-[#1d1926]/60 leading-relaxed max-w-md"
            >
              商材分析から訴求設計、バナー生成、サイズ展開まで全自動。
              制作コスト1/50、1枚約45円〜、数分で完成。
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/signup"
                onClick={() => track(ANALYTICS_EVENTS.CTA_CLICKED, { cta_id: "hero_signup", location: "landing_hero" })}
                className="inline-flex items-center h-[52px] px-8 rounded-full bg-[#3eb298] text-white text-[16px] font-semibold hover:opacity-90 transition-opacity no-underline"
              >
                無料で試す
              </Link>
              <Link
                to="/diagnose"
                onClick={() => track(ANALYTICS_EVENTS.CTA_CLICKED, { cta_id: "hero_diagnose", location: "landing_hero" })}
                className="inline-flex items-center h-[52px] px-8 rounded-full border border-[rgba(29,25,38,0.14)] text-[#1d1926] text-[16px] font-medium hover:bg-white/50 transition-colors no-underline"
              >
                バナーを作ってみる
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mt-8 flex items-center gap-6 text-[14px] text-[#1d1926]/40"
            >
              <span className="flex items-center gap-1.5">
                <CheckIcon />
                クレカ不要で開始
              </span>
              <span className="flex items-center gap-1.5">
                <CheckIcon />
                月額9,800円〜
              </span>
              <span className="flex items-center gap-1.5">
                <CheckIcon />
                デザインスキル不要
              </span>
            </motion.div>
          </div>

          {/* Mobile: Simple product screenshot */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:hidden rounded-2xl overflow-hidden border border-[rgba(29,25,38,0.08)] shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
          >
            <img
              src={`${publicUrl}/lp/screenshots/ui-banner-gallery.png`}
              alt="Tasky ダッシュボード"
              className="w-full h-auto"
            />
          </motion.div>

          {/* Right: Product UI + Banner Collage */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="relative w-full aspect-square max-w-[560px] ml-auto">
              {/* Dashboard screenshot as base */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden border border-[rgba(29,25,38,0.08)] shadow-[0_8px_40px_rgba(0,0,0,0.06)]">
                <img
                  src={`${publicUrl}/lp/screenshots/ui-banner-gallery.png`}
                  alt="Tasky ダッシュボード"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Floating banner cards */}
              {bannerImages.slice(0, 4).map((src, i) => {
                const positions = [
                  {
                    top: "-4%",
                    right: "-6%",
                    rotate: 3,
                    w: 140,
                    h: 140,
                  },
                  {
                    bottom: "8%",
                    right: "-8%",
                    rotate: -2,
                    w: 130,
                    h: 130,
                  },
                  {
                    bottom: "-4%",
                    left: "10%",
                    rotate: 2,
                    w: 120,
                    h: 120,
                  },
                  {
                    top: "12%",
                    left: "-6%",
                    rotate: -3,
                    w: 110,
                    h: 110,
                  },
                ];
                const pos = positions[i];
                return (
                  <motion.div
                    key={src}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                    className="absolute rounded-xl overflow-hidden border border-white/60 shadow-[0_4px_20px_rgba(0,0,0,0.1)]"
                    style={{
                      ...pos,
                      width: pos.w,
                      height: pos.h,
                      transform: `rotate(${pos.rotate}deg)`,
                    }}
                  >
                    <img
                      src={`${publicUrl}${src}`}
                      alt="生成バナーサンプル"
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
